import { defineStore } from 'pinia';
import { login as apiLogin, getMe } from '../api';
import { createToastInterface } from 'vue-toastification';

const toast = createToastInterface();


// Define the User type according to your backend model
interface User {
  id: number;
  fullName: string;
  email: string;
  role: 'Super Admin' | 'Admin' | 'Manager' | 'Agent' | 'User' | 'cmoUser';
  cmoCanEdit?: boolean;
  designation?: string;
  division?: string;
  profilePicture?: string;
  status: 'Active' | 'Inactive';
  phoneNumber?: string;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('token') || null as string | null,
    user: JSON.parse(localStorage.getItem('user') || 'null') as User | null,
    returnUrl: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    userRole: (state) => state.user?.role,
    isSuperAdmin: (state) => state.user?.role === 'Super Admin',
    isAdmin: (state) => state.user?.role === 'Admin',
    isManager: (state) => state.user?.role === 'Manager',
    isCmoUser: (state) => state.user?.role === 'cmoUser',
    canEditCMO: (state) => state.user?.role === 'Super Admin' || (state.user?.role === 'cmoUser' && !!state.user?.cmoCanEdit),
    canManageUsers: (state) => {
      const role = state.user?.role;
      return role === 'Super Admin' || role === 'Admin' || role === 'Manager';
    },
    canCreateContent: (state) => {
      const role = state.user?.role;
      return role === 'Super Admin' || role === 'Admin' || role === 'Manager';
    },
    canDeleteData: (state) => {
      const role = state.user?.role;
      return role === 'Super Admin' || role === 'Admin';
    }
  },

  actions: {
    async login(credentials: any) {
      try {
        const response = await apiLogin(credentials);
        
        const { token, user } = response.data;
        
        if (!token || !user) {
          throw new Error('Invalid response from server');
        }
        
        this.token = token;
        this.user = user;

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        toast.success('Login successful!');

        // Force a router push to dashboard and replace the current history entry
        
        // Force reload the page to ensure proper state
      } catch (error: any) {
        console.error('Auth store: Login failed:', error);
        
        // Don't show toast here as it's handled in the component
        // Re-throw the error to be caught in the component
        throw error;
      }
    },

    logout() {
      this.token = null;
      this.user = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },

    async fetchUser() {
      if (this.token && !this.user) {
        try {
          const response = await getMe();
          this.user = response.data;
          localStorage.setItem('user', JSON.stringify(this.user));
        } catch (error) {
          // Token might be invalid, so log out
          console.error('Failed to fetch user:', error);
          this.logout();
        }
      }
    },

    // Action to update user data in the store
    updateUserProfile(updatedUser: Partial<User>) {
      if (this.user) {
        this.user = { ...this.user, ...updatedUser };
        localStorage.setItem('user', JSON.stringify(this.user));
      }
    }
  },
});